/**
 * Google Calendar Integration Service
 *
 * Provides functionality for integrating with Google Calendar's appointment scheduling
 * Similar to Calendly but using Google's native booking pages feature.
 */

import { apiClient } from './apiClient';

export interface CalendarEventData {
  summary: string;
  description: string;
  startTime: string;
  endTime: string;
  attendeeEmail: string;
  attendeeName: string;
  location?: string;
  meetingType: 'google-meet' | 'zoom' | 'phone' | 'in-person';
}

export interface BookingPageConfig {
  title: string;
  description: string;
  duration: number; // in minutes
  bufferTime: number; // buffer between meetings in minutes
  availability: {
    timeZone: string;
    workingHours: {
      start: string; // HH:MM format
      end: string; // HH:MM format
    };
    workingDays: number[]; // 0-6 (Sunday to Saturday)
  };
}

export interface CalendarBookingResponse {
  bookingUrl: string;
  eventId?: string;
  success: boolean;
  error?: string;
}

class CalendarService {
  private readonly baseUrl = import.meta.env.VITE_API_BASE_URL;
  private readonly calendarId = import.meta.env.VITE_GOOGLE_CALENDAR_ID;

  /**
   * Creates a Google Calendar booking page for demo appointments
   */
  async createDemoBookingPage(
    userEmail: string,
    userName: string,
    customConfig?: Partial<BookingPageConfig>,
  ): Promise<CalendarBookingResponse> {
    try {
      const defaultConfig: BookingPageConfig = {
        title: 'Community App Demo Session',
        description: `Schedule a personalized demo with our team to explore how our community platform can benefit your organization.`,
        duration: 30,
        bufferTime: 15,
        availability: {
          timeZone: 'America/New_York', // Default timezone
          workingHours: {
            start: '09:00',
            end: '17:00',
          },
          workingDays: [1, 2, 3, 4, 5], // Monday to Friday
        },
      };

      const config = { ...defaultConfig, ...customConfig };

      // Use the actual Google Calendar appointment scheduling URL
      const bookingUrl =
        'https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ1LCP3XRx7RBNvrOvV9RQdYWU5OEzKRS5tkgmf4vU46PSnbPJMPjAj74Mh70b2V30ev-mcT6wq0?gv=true';

      // Add user info as URL parameters to pre-fill the form
      const urlWithParams = new URL(bookingUrl);
      urlWithParams.searchParams.set('prefill_email', userEmail);
      urlWithParams.searchParams.set('prefill_name', userName);

      return {
        bookingUrl: urlWithParams.toString(),
        success: true,
      };
    } catch (error) {
      console.error('Error creating calendar booking page:', error);
      return {
        bookingUrl: '',
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  /**
   * Creates a direct calendar event (alternative to booking page)
   */
  async createDirectEvent(eventData: CalendarEventData): Promise<CalendarBookingResponse> {
    try {
      const response = await apiClient.post<CalendarBookingResponse>('/calendar/create-event', {
        calendarId: this.calendarId,
        ...eventData,
      });

      return response;
    } catch (error) {
      console.error('Error creating calendar event:', error);
      return {
        bookingUrl: '',
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  /**
   * Generate a pre-filled Google Calendar link for manual booking
   * This is a fallback option that doesn't require API access
   */
  generateManualBookingLink(
    title: string,
    description: string,
    attendeeEmail: string,
    duration: number = 30,
  ): string {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() + 1); // Tomorrow
    startDate.setHours(10, 0, 0, 0); // 10 AM

    const endDate = new Date(startDate);
    endDate.setMinutes(endDate.getMinutes() + duration);

    const formatDateTime = (date: Date): string => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: title,
      dates: `${formatDateTime(startDate)}/${formatDateTime(endDate)}`,
      details: description,
      add: attendeeEmail,
      location: 'Google Meet (link will be provided)',
    });

    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  }

  /**
   * Validate calendar integration is properly configured
   */
  async validateConfiguration(): Promise<boolean> {
    try {
      // Since we're using a direct Google Calendar booking URL, we're always configured
      return true;
    } catch (error) {
      console.error('Calendar service configuration validation failed:', error);
      return false;
    }
  }
}

export const calendarService = new CalendarService();
export default calendarService;
